import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './ProductDisplay.css'
import { ShopContext } from '../../Context/ShopContext'
import { getOptimizedImageUrl, setOptimizedImageFallback } from '../../utils/imageOptimization'

const ProductDisplay = (props) => {
    const { product } = props;
    const { addToCart } = useContext(ShopContext);
    const [selectedSize, setSelectedSize] = useState('');
    const [sizeError, setSizeError] = useState('');
    const [selectedImage, setSelectedImage] = useState(0);
    const navigate = useNavigate();

    // Add safety checks for the product data
    if (!product) {
        return <div className='productdisplay'>Product not found</div>;
    }

    if (!product.name) {
        console.error("Invalid product data:", product);
        return <div className='productdisplay'>Invalid product data</div>;
    }

    // Get all images
    const getAllImages = () => {
        const images = [];
        
        if (product.main_image) {
            images.push(product.main_image);
        }
        
        if (product.additional_images && product.additional_images.length > 0) {
            images.push(...product.additional_images.filter(img => img));
        }
        
        if (images.length === 0 && product.images && product.images.length > 0) {
            images.push(...product.images.filter(img => img));
        }
        
        if (images.length === 0 && product.image) {
            images.push(product.image);
        }
        
        return images;
    };

    const allImages = getAllImages();

    // Get available sizes - use product.sizes if available, otherwise use defaults
    const getAvailableSizes = () => {
        if (product.sizes && product.sizes.length > 0) {
            return product.sizes;
        }
        
        // Default sizes based on product type
        if (product.productType === 'shoes') {
            // Shoe sizes (38-49)
            return Array.from({length: 12}, (_, i) => (38 + i).toString());
        } else {
            // Clothing sizes
            return ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
        }
    };

    const availableSizes = getAvailableSizes();

    // Handle size selection
    const handleSizeSelect = (size) => {
        setSelectedSize(size);
        setSizeError('');
    }

    // Handle image selection
    const handleImageSelect = (index) => {
        setSelectedImage(index);
    }

    // Handle next image
    const handleNextImage = () => {
        setSelectedImage((prev) => (prev + 1) % allImages.length);
    }

    // Handle previous image
    const handlePrevImage = () => {
        setSelectedImage((prev) => (prev - 1 + allImages.length) % allImages.length);
    }

    // Handle add to cart with validation - UPDATED: No redirect
    const handleAddToCart = async () => {
        if (!selectedSize) {
            setSizeError('Please select a size');
            return;
        }
        
        try {
            // Add to cart with product ID and size only (no color)
            const success = await addToCart(product.id, selectedSize, '');
            
            if (success) {
                // Only add to cart, do NOT redirect
                // You can optionally show a success message here
                console.log('Item added to cart successfully');
            } else {
                console.error('Failed to add item to cart');
                setSizeError('Failed to add item to cart. Please try again.');
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            setSizeError('An error occurred while adding to cart');
        }
    }

    // Handle "Buy Now" - Add to cart and immediately redirect to cart page - UPDATED
    const handleBuyNow = async () => {
        if (!selectedSize) {
            setSizeError('Please select a size');
            return;
        }
        
        try {
            // Add to cart with product ID and size only (no color)
            const success = await addToCart(product.id, selectedSize, '');
            
            if (success) {
                // Redirect to cart page
                navigate('/cart');
            } else {
                console.error('Failed to add item to cart');
                setSizeError('Failed to add item to cart. Please try again.');
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            setSizeError('An error occurred while adding to cart');
        }
    }

    // Get product type display name
    const getProductTypeDisplay = () => {
        return product.productType === 'shoes' ? 'Shoes' : 'Clothing';
    }

    return (
        <div className='productdisplay'>
            <div className="productdisplay-left">
                <div className="productdisplay-img-list">
                    {allImages.map((image, index) => (
                        <img 
                            key={index}
                            src={getOptimizedImageUrl(image, { width: 220, quality: 70 })} 
                            alt={`${product.name} view ${index + 1}`}
                            className={selectedImage === index ? 'active' : ''}
                            loading="lazy"
                            decoding="async"
                            fetchpriority="low"
                            onError={(e) => setOptimizedImageFallback(e, image)}
                            onClick={() => handleImageSelect(index)}
                        />
                    ))}
                </div>
                <div className="productdisplay-img">
                    {allImages.length > 1 && (
                        <>
                            <button 
                                className="image-nav-btn prev-btn" 
                                onClick={handlePrevImage}
                                aria-label="Previous image"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <button 
                                className="image-nav-btn next-btn" 
                                onClick={handleNextImage}
                                aria-label="Next image"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </>
                    )}
                    <div className="image-counter">
                        {selectedImage + 1} / {allImages.length}
                    </div>
                    <img 
                        className='productdisplay-main-img' 
                        src={getOptimizedImageUrl(allImages[selectedImage] || product.image, { width: 900, quality: 75 })}
                        alt={product.name}
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                        onError={(e) => setOptimizedImageFallback(e, allImages[selectedImage] || product.image)}
                    />
                </div>
            </div>
            <div className="productdisplay-right">
                <h1>{product.name}</h1>
                
                {/* Product Type Badge */}
                <div className="product-type-badge">
                    <span className={`badge ${product.productType || 'clothing'}`}>
                        {getProductTypeDisplay()}
                    </span>
                </div>
                
               
                <div className="productdisplay-right-prices">
                    <div className="productdisplay-right-price-old">Rs {product.old_price}</div>
                    <div className="productdisplay-right-price-new">Rs {product.new_price}</div>
                </div>
              
                
                {/* Additional Details */}
                <div className="product-details-grid">
                    {product.material && (
                        <div className="detail-item">
                            <span className="detail-label">Material:</span>
                            <span className="detail-value">{product.material}</span>
                        </div>
                    )}
                    {product.brand && (
                        <div className="detail-item">
                            <span className="detail-label">Brand:</span>
                            <span className="detail-value">{product.brand}</span>
                        </div>
                    )}
                </div>

                {/* Size Selection Section */}
                <div className="productdisplay-right-size">
                    <h1>Select Size {product.productType === 'shoes' ? '(EU)' : ''}</h1>
                    <div className={`productdisplay-right-sizes ${product.productType === 'shoes' ? 'shoe-sizes' : 'clothing-sizes'}`}>
                        {availableSizes.map((size) => (
                            <div 
                                key={size}
                                className={`size-option ${selectedSize === size ? 'selected' : ''}`}
                                onClick={() => handleSizeSelect(size)}
                            >
                                {size}
                            </div>
                        ))}
                    </div>
                    {sizeError && <div className="error-message">{sizeError}</div>}
                </div>

                <div className="product-action-buttons">
                    <button 
                        className="btn-add-to-cart" 
                        onClick={handleAddToCart}
                    >
                        ADD TO CART
                    </button>
                    
                    <button 
                        className="btn-buy-now"
                        onClick={handleBuyNow}
                    >
                        BUY NOW
                    </button>
                </div>
                
                <div className='descriptionbox'>
                    <div className="descriptionbox-navigator">
                        <div className="descriptionbox-nav-box">Description</div>
                    </div>
                    <div className="descriptionbox-description">
                        <p>
                            <div className="productdisplay-right-description">
                                {product.description || "Product description not available."}
                            </div>
                        </p>
                    </div>
                </div>
               
                {product.care_instructions && (
                    <div className="care-instructions">
                        <span>Care Instructions:</span> {product.care_instructions}
                    </div>
                )}
            </div>
        </div>
    )
}

export default ProductDisplay